/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import downloadImg from './dndimages/download-icon.png';
import Monster from './model/Monster';
import { FantasyGroundService } from './services/FantasyGroundService';
import './DownloadVttFormatButton.css';

interface DownloadVttFormatButtonProps {
  getData: () => Monster;  // Getter function instead of direct data
  filename: string;
}

type VttType = 'fantasy-grounds' | 'roll20' | 'foundry';

interface VttOption {
  id: VttType;
  name: string;
  abbreviation: string;
  enabled: boolean;
}

const vttOptions: VttOption[] = [
  { id: 'fantasy-grounds', name: 'Fantasy Grounds', abbreviation: 'FG',      enabled: true },
  { id: 'roll20',          name: 'Roll20',          abbreviation: 'R20',     enabled: false },
  { id: 'foundry',         name: 'Foundry VTT',     abbreviation: 'Foundry', enabled: false },
];

const DownloadVttFormatButton: React.FC<DownloadVttFormatButtonProps> = ({ getData, filename }) => {
  const [selectedVtt, setSelectedVtt] = useState<VttType>('fantasy-grounds');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load saved preference
  useEffect(() => {
    const saved = localStorage.getItem('preferredVtt');
    if (saved && vttOptions.some(opt => opt.id === saved)) {
      setSelectedVtt(saved as VttType);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleDownload = async () => {
    const monster = getData();

    switch (selectedVtt) {
      case 'fantasy-grounds':
        await FantasyGroundService.exportMonsterToXml(getData());
        break;
      case 'roll20':
        alert('Roll20 export coming soon!');
        break;
      case 'foundry':
        alert('Foundry VTT export coming soon!');
        break;
    }
  };

  const handleVttSelect = (vttId: VttType) => {
    const option = vttOptions.find(opt => opt.id === vttId);
    if (!option?.enabled) {
      alert(`${option?.name} export is coming soon!`);
      return;
    }

    setSelectedVtt(vttId);
    localStorage.setItem('preferredVtt', vttId);
    setIsDropdownOpen(false);
  };

  const selectedOption = vttOptions.find(opt => opt.id === selectedVtt)!;
  const fileType = 'xml';

  return (
    <div className="vtt-button-container" id="downloadVttButton" ref={dropdownRef}>
      <button onClick={handleDownload} className="vtt-export-button-main"
              title={`Download monster ${fileType} file for ${selectedOption.name}`}
      >
        <img src={downloadImg} alt="Download" width="16px" />
        Export to {selectedOption.abbreviation}
      </button>
      <button onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="vtt-export-button-dropdown" title="Choose VTT platform">▼</button>

      {isDropdownOpen && (
        <div className="vtt-dropdown-menu">
          {vttOptions.map(option => (
            <div
              key={option.id}
              className={`vtt-dropdown-item ${option.id === selectedVtt ? 'selected' : ''} ${!option.enabled ? 'disabled' : ''}`}
              onClick={() => handleVttSelect(option.id)}
            >
              <span className="vtt-name">{option.name}</span>
              {option.id === selectedVtt && <span className="vtt-checkmark">✓</span>}
              {!option.enabled && <span className="vtt-badge">Coming Soon</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DownloadVttFormatButton;
